import { Building2, CheckCircle2, Clock, Receipt } from "lucide-react";
import { useDashboard } from "@/api/hooks";
import { type TenantSummary, type TenantStatus } from "@/api/portalApi";
import { cn } from "@/lib/utils";

// ── Badge helpers ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: TenantStatus }) {
    const styles: Record<TenantStatus, string> = {
        Active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
        Pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
        Suspended: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
        Cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
    };
    return (
        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", styles[status])}>
            {status}
        </span>
    );
}

function PlanBadge({ plan }: { plan: string }) {
    const styles: Record<string, string> = {
        Starter: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
        Business: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
        Enterprise: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
    };
    return (
        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", styles[plan] ?? "bg-gray-100 text-gray-600")}>
            {plan}
        </span>
    );
}

function formatDate(value: string) {
    return new Date(value).toLocaleDateString(undefined, { dateStyle: "medium" });
}

function formatCurrency(amount: number) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

// ── Stat card ─────────────────────────────────────────────────────────────────

interface StatCardProps {
    icon: React.ReactNode;
    label: string;
    value: string | number;
    sub?: string;
    accent?: string;
}

function StatCard({ icon, label, value, sub, accent }: StatCardProps) {
    return (
        <section className="rounded-lg border bg-card p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
                <span className={cn("flex h-8 w-8 items-center justify-center rounded-full bg-muted", accent)}>
                    {icon}
                </span>
                <span>{label}</span>
            </div>
            <div className="text-2xl font-semibold tabular-nums">{value}</div>
            {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
        </section>
    );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function Skeleton({ className }: { className?: string }) {
    return <div className={cn("animate-pulse rounded bg-muted", className)} />;
}

function DashboardSkeleton() {
    return (
        <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="rounded-lg border bg-card p-5">
                        <Skeleton className="mb-3 h-8 w-8 rounded-full" />
                        <Skeleton className="h-7 w-16" />
                    </div>
                ))}
            </div>
            <div className="rounded-lg border bg-card p-5">
                <Skeleton className="mb-4 h-5 w-40" />
                <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-9 w-full" />)}
                </div>
            </div>
        </div>
    );
}

// ── Recent enrollments table ──────────────────────────────────────────────────

function RecentEnrollmentsTable({ enrollments }: { enrollments: TenantSummary[] }) {
    return (
        <section className="rounded-lg border bg-card shadow-sm">
            <div className="border-b px-5 py-4">
                <h2 className="font-semibold">Recent Enrollments</h2>
                <p className="text-sm text-muted-foreground">Last 5 organisations that enrolled</p>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b bg-muted/40 text-left text-xs font-medium text-muted-foreground">
                            <th className="px-4 py-2.5">Company</th>
                            <th className="px-4 py-2.5">Domain</th>
                            <th className="px-4 py-2.5">Plan</th>
                            <th className="px-4 py-2.5">Status</th>
                            <th className="px-4 py-2.5">Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {enrollments.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                                    No recent enrollments
                                </td>
                            </tr>
                        ) : (
                            enrollments.map((t) => (
                                <tr key={t.id} className="border-b last:border-0 hover:bg-muted/20">
                                    <td className="px-4 py-2.5 font-medium">{t.companyName}</td>
                                    <td className="px-4 py-2.5 font-mono text-xs">{t.domain}</td>
                                    <td className="px-4 py-2.5"><PlanBadge plan={t.plan} /></td>
                                    <td className="px-4 py-2.5"><StatusBadge status={t.status} /></td>
                                    <td className="px-4 py-2.5 text-muted-foreground">{formatDate(t.createdAt)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function DashboardPage() {
    const { data, isLoading, error } = useDashboard();

    if (isLoading) return <DashboardSkeleton />;

    if (error) {
        return (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                Failed to load dashboard: {error instanceof Error ? error.message : "Unknown error"}
            </div>
        );
    }

    if (!data) return null;

    return (
        <div className="space-y-6">
            {/* Stat cards */}
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard
                    icon={<Building2 className="h-4 w-4 text-foreground/70" />}
                    label="Total Tenants"
                    value={data.totalTenants}
                />
                <StatCard
                    icon={<CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />}
                    label="Active Tenants"
                    value={data.activeTenants}
                    accent="text-green-600 dark:text-green-400"
                />
                <StatCard
                    icon={<Receipt className="h-4 w-4 text-amber-600 dark:text-amber-400" />}
                    label="Pending Invoices"
                    value={data.pendingInvoiceCount}
                    sub={data.pendingInvoiceCount > 0 ? `${formatCurrency(data.pendingInvoiceTotal)} total` : undefined}
                    accent="text-amber-600 dark:text-amber-400"
                />
                <StatCard
                    icon={<Clock className="h-4 w-4 text-primary" />}
                    label="Pending Review"
                    value={data.pendingTenants}
                    sub="Awaiting activation"
                    accent="text-primary"
                />
            </div>

            {/* Recent enrollments */}
            <RecentEnrollmentsTable enrollments={data.recentEnrollments} />
        </div>
    );
}
